import SectionReveal from './SectionReveal';
import { HostedReportSectionLayout } from '../HostedReportSectionLayout';
import type { ReportHostedSectionProps } from '../reportHostedSection.types';
import {
  LOOKING_AHEAD_CLOSING_PARAGRAPH,
  LOOKING_AHEAD_HEADLINE,
  LOOKING_AHEAD_OVERLINE,
  buildLookingAheadLeadParagraph,
  buildLookingAheadPreparedBy,
  resolveLookingAheadEngagementRatePct,
  resolveUsStateLabel,
} from '@act/shared/data-transfer-objects';
import { body, display, muted } from './tokens';

const readString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value : null;

/**
 * Looking Ahead closing section with dynamic state, engagement rate, and footer.
 * @param props - Resolved snapshot, customer context, and reporting period.
 */
const LookingAhead = ({
  data,
  customer,
  reportingPeriod,
}: ReportHostedSectionProps) => {
  const stateName =
    readString(data.stateName) ?? resolveUsStateLabel(customer.usStateCode);
  const engagementRatePct = resolveLookingAheadEngagementRatePct(
    customer,
    reportingPeriod,
    typeof data.engagementRatePct === 'number' ? data.engagementRatePct : null,
  );
  const periodLabel = readString(data.reportingPeriod) ?? reportingPeriod;
  const overline = readString(data.overline) ?? LOOKING_AHEAD_OVERLINE;
  const headline = readString(data.headline) ?? LOOKING_AHEAD_HEADLINE;
  const leadParagraph =
    readString(data.leadParagraph) ??
    buildLookingAheadLeadParagraph(stateName, engagementRatePct);
  const closingParagraph =
    readString(data.closingParagraph) ?? LOOKING_AHEAD_CLOSING_PARAGRAPH;
  const preparedBy = buildLookingAheadPreparedBy(customer.name, periodLabel);

  return (
    <HostedReportSectionLayout
      sectionDomId="looking-ahead"
      className="section--tan report-looking-ahead-section"
      style={{ background: 'var(--reema-tan)', fontFamily: body }}
      innerClassName="container container--narrow report-looking-ahead-section__inner"
      banner={null}
    >
      <div className="report-looking-ahead-section__main">
        <SectionReveal>
          <div
            className="report-looking-ahead-section__content"
            style={{ textAlign: 'center' }}
          >
            <span
              className="overline"
              style={{ display: 'block', marginBottom: 16 }}
            >
              {overline}
            </span>
            <h2
              className="heading-xl"
              style={{
                marginBottom: 28,
                fontFamily: display,
                fontWeight: 600,
                color: 'var(--reema-midnight-100)',
              }}
            >
              {headline}
            </h2>
            <p
              className="lede"
              style={{
                marginBottom: 16,
                fontFamily: body,
                fontSize: '1.125rem',
                lineHeight: 1.55,
                color: muted,
              }}
            >
              {leadParagraph}
            </p>
            <p
              className="lede report-looking-ahead-section__closing"
              style={{
                marginBottom: 0,
                fontFamily: body,
                fontSize: '1.125rem',
                lineHeight: 1.55,
                color: muted,
              }}
            >
              {closingParagraph}
            </p>
          </div>
        </SectionReveal>
      </div>
      <div className="report-footer report-looking-ahead-section__footer">
        <div className="report-footer__pill">
          <div className="report-footer__pulse" aria-hidden />
          {preparedBy}
        </div>
      </div>
    </HostedReportSectionLayout>
  );
};

export default LookingAhead;
